
export function adaptDatabaseToDerivativeCreation(item: any) {
  // Debug the raw item to see what we're working with
  console.log(`adaptDatabaseToDerivativeCreation: Processing item ID ${item.id}`);
  console.log('Raw item data:', {
    id: item.id,
    views: item.views,
    likes_count: item.likes_count,
    favorites_count: item.favorites_count,
    comments_count: item.comments_count
  });
  
  // Extract creator name using a prioritized approach
  let creatorName = "ReCut Creator";
  let creatorAvatar = "";
  
  // IMPROVED PROFILE HANDLING: Better debugging and creator name resolution
  // Check if we have profiles data in the expected format
  if (item.profiles) {
    console.log(`adaptDatabaseToDerivativeCreation: Found profiles data for ${item.id}:`, item.profiles);
    
    // Clear prioritization of username > full_name
    if (item.profiles.username) {
      creatorName = item.profiles.username;
      console.log(`Using username: ${creatorName}`);
    } else if (item.profiles.full_name) {
      creatorName = item.profiles.full_name;
      console.log(`Using full_name: ${creatorName}`);
    } else {
      console.log(`No username or full_name found in profiles for ${item.id}`);
    }
    
    creatorAvatar = item.profiles.avatar_url || "";
  } else {
    // Fallback strategies if profiles data is not in the expected format
    console.log(`No profiles object found for ${item.id}, checking other fields`);
    
    // Check for creator_name (snake_case from DB)
    if (item.creator_name) {
      creatorName = item.creator_name;
      creatorAvatar = item.creator_avatar || "";
      console.log(`Using creator_name: ${creatorName}`);
    } 
    // Check for creatorName (camelCase, possibly from transformed data)
    else if (item.creatorName) {
      creatorName = item.creatorName;
      creatorAvatar = item.creatorAvatar || "";
      console.log(`Using creatorName: ${creatorName}`);
    }
    // Check for username (often from profiles table join)
    else if (item.username) {
      creatorName = item.username;
      console.log(`Using username: ${creatorName}`);
    }
    // Check for full_name (often from profiles table join)
    else if (item.full_name) {
      creatorName = item.full_name;
      console.log(`Using full_name: ${creatorName}`);
    }
    // Final fallback to original_creator 
    else if (item.original_creator && item.original_creator !== "Original Creator") {
      creatorName = item.original_creator;
      console.log(`Using original_creator: ${creatorName}`);
    } else {
      console.log(`No creator information found for ${item.id}, using default`);
    }
  }
  
  // Ensure we don't use "Original Creator" as a display name
  if (creatorName === "Original Creator" || !creatorName) {
    creatorName = "ReCut Creator";
    console.log(`Reset invalid creator name to default for ${item.id}`);
  }
  
  // Ensure we have a valid video URL
  const videoUrl = item.video_url || item.videoUrl || "";
  if (!videoUrl) {
    console.warn(`adaptDatabaseToDerivativeCreation: Missing video URL for derivative ${item.id}`);
  }

  const aspectRatio = typeof item.aspect_ratio === 'number' ? 
                     item.aspect_ratio : 
                     (typeof item.aspectRatio === 'number' ? item.aspectRatio : 16/9);

  // Get social metrics from enhanced view - add detailed logging and improved type checking
  console.log(`Social metrics for ${item.id}:`, {
    raw_views: item.views,
    raw_likes_count: item.likes_count,
    raw_likes_count_camel: item.likesCount,
    raw_comments_count: item.comments_count,
    raw_comments_count_camel: item.commentsCount,
    raw_favorites_count: item.favorites_count, 
    raw_favorites_count_camel: item.favoritesCount
  });

  // Force metrics to be numbers with fallback to 0
  const views = typeof item.views === 'number' ? item.views : 
               (typeof item.views === 'string' ? parseInt(item.views, 10) || 0 : 0);
  
  const likesCount = typeof item.likes_count === 'number' ? item.likes_count : 
                    (typeof item.likesCount === 'number' ? item.likesCount : 
                    (typeof item.likes_count === 'string' ? parseInt(item.likes_count, 10) || 0 : 0));
                     
  const commentsCount = typeof item.comments_count === 'number' ? item.comments_count : 
                       (typeof item.commentsCount === 'number' ? item.commentsCount : 
                       (typeof item.comments_count === 'string' ? parseInt(item.comments_count, 10) || 0 : 0));
                       
  const favoritesCount = typeof item.favorites_count === 'number' ? item.favorites_count : 
                        (typeof item.favoritesCount === 'number' ? item.favoritesCount : 
                        (typeof item.favorites_count === 'string' ? parseInt(item.favorites_count, 10) || 0 : 0));

  const result = {
    id: item.id,
    creatorId: item.user_id || item.creator_id || item.creatorId || "",
    creatorName: creatorName,
    creatorAvatar: item.creator_avatar || item.creatorAvatar || creatorAvatar,
    clipId: item.clip_id || item.original_clip_id || item.clipId || "",
    title: item.title || "",
    description: item.description || "",
    thumbnail: item.thumbnail_url || item.thumbnail || item.thumbnailUrl || "",
    videoUrl: item.video_url || item.videoUrl || "",
    expressionType: item.expression_type || item.expressionType || "edit",
    aspectRatio: aspectRatio,
    status: item.status || "pending",
    submittedAt: item.submitted_date || item.submitted_at || item.submittedAt || new Date().toISOString(),
    approvedAt: item.approved_date || item.approved_at || item.approvedAt || null,
    commentsCount: commentsCount,
    likesCount: likesCount,
    favoritesCount: favoritesCount,
    views: views
  };
  
  console.log(`adaptDatabaseToDerivativeCreation: FINAL RESULT for ${item.id}:`, {
    views: result.views, 
    likes: result.likesCount,
    favorites: result.favoritesCount,
    comments: result.commentsCount
  });
  
  return result;
}

// Add these adapter functions matching the expected field names in DerivativeDetail

export function adaptDatabaseToDerivativeDetail(item: any) {
  console.log(`adaptDatabaseToDerivativeDetail: Processing item ID ${item.id}`);
  
  // Use the existing adapter and add more detailed properties
  const baseDerivative = adaptDatabaseToDerivativeCreation(item);
  
  return {
    ...baseDerivative,
    thumbnailUrl: baseDerivative.thumbnail,  // Make sure thumbnailUrl exists as it's required by DerivativeDetail
    views: item.views || 0,
    likes: item.likes || 0,
    createdAt: item.created_at || item.submitted_date || item.submitted_at || new Date().toISOString(),
    updatedAt: item.updated_at || new Date().toISOString(),
    rejectedAt: item.rejected_date || item.rejected_at || null,
    playlistId: item.playlist_id || null,
    playlistName: item.playlist_name || null,
    originalThumbnailUrl: item.original_thumbnail || null,
    originalTitle: item.original_title || null,
    channelId: item.channel_id || null,
    channelName: item.channel_name || null,
    rejectionReason: item.rejection_reason || item.review_comment || null,
    originalClips: item.original_clips || [],
    social_links: item.social_links || null,
    userId: item.user_id || null
  };
}

export function adaptDatabaseToDerivativeListItem(item: any) {
  console.log(`adaptDatabaseToDerivativeListItem: Processing item ID ${item.id}`);
  
  // Use the core adapter with list-specific fields
  const baseDerivative = adaptDatabaseToDerivativeCreation(item);
  
  return {
    id: baseDerivative.id,
    title: baseDerivative.title,
    description: baseDerivative.description,
    thumbnailUrl: baseDerivative.thumbnail,
    videoUrl: baseDerivative.videoUrl,
    expressionType: baseDerivative.expressionType,
    aspectRatio: baseDerivative.aspectRatio,
    creatorId: baseDerivative.creatorId,
    creatorName: baseDerivative.creatorName,
    creatorAvatar: baseDerivative.creatorAvatar,
    status: baseDerivative.status,
    submittedAt: baseDerivative.submittedAt,
    clipId: baseDerivative.clipId,
    userId: item.user_id || null,
    approvedAt: baseDerivative.approvedAt,
    commentsCount: baseDerivative.commentsCount,
    likesCount: baseDerivative.likesCount,
    favoritesCount: baseDerivative.favoritesCount,
    views: baseDerivative.views
  };
}
